import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import { ApiError } from "../../api/errors";
import {
  DOCUMENT_KINDS, DOCUMENT_TITLES, documentProblem, listDocuments, MAX_DOCUMENT_CHARS, normaliseDocument, saveDocument,
  type DocumentKind, type OperatorDocument,
} from "../../api/contact";
import { LEGAL_DOCS } from "../../routes/LegalDoc";
import { remainingPlaceholders } from "./placeholders";
import { SUBSCRIBER_TERMS_TEMPLATE } from "./template";

/**
 * Legal & Terms: the provider's own documents for its guests, and a list of
 * AnkhOS's documents for the provider.
 *
 * 🔑 TWO DIFFERENT RELATIONSHIPS ON ONE PAGE. The editable documents are the
 * provider's, issued in its own name to its subscribers, and shown on its
 * captive portal under Legal & Terms. AnkhOS is not a party to them. AnkhOS's
 * own terms bind the provider, are authored and approved in io.ankhos, and are
 * only linked here.
 *
 * Stored once per namespace in radaccess and shown with every theme; a child
 * namespace without its own document shows its parent's. Design: radius-wiki
 * portal-brand-from-logo.md, "Page background, layout, contact and legal pages" §3.
 */

const refusal = (e: unknown) =>
  e instanceof ApiError && e.kind === "forbidden"
    ? "Refused: your role may not change this namespace's documents, or it is outside your subtree."
    : e instanceof ApiError && e.kind === "not-found"
      ? "The documents service is not available yet (radaccess v1.52)."
      : e instanceof Error
        ? e.message
        : String(e);

export function LegalDocumentsScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const load = useQuery({
    queryKey: ["operatorDocuments", ns],
    enabled: Boolean(identity.token && ns),
    retry: false,
    queryFn: async () => (await listDocuments(identity, ns)).data.documents ?? [],
  });

  if (!ns) {
    return <Alert severity="error">No namespace could be resolved from your sign-in, so there are no documents to edit.</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 1100 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Legal &amp; Terms</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Documents your guests open from the footer of your captive portal for <b>{ns}</b>. They are the same for every theme.
        Plain text: a blank line starts a new paragraph.
      </Typography>
      <Alert severity="info" sx={{ mb: 3 }}>
        Payment gateways expect your prices, Terms of Service, Privacy Policy and Cancellation &amp; Refunds policy to be reachable
        before a guest pays. A document you leave empty is not listed on the portal.
      </Alert>

      {load.isLoading && <Typography variant="body2">Loading…</Typography>}
      {load.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Could not load your documents</AlertTitle>
          {refusal(load.error)}
        </Alert>
      )}
      {load.data &&
        DOCUMENT_KINDS.map((kind) => (
          <DocumentEditor key={kind} ns={ns} kind={kind} doc={load.data.find((d) => d.kind === kind) ?? null} />
        ))}

      <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
        <Typography variant="overline" color="text.secondary">AnkhOS documents</Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          AnkhOS's own terms and pricing for providers. They are between AnkhOS and you, and do not bind your guests.
        </Typography>
        <Stack direction="row" useFlexGap spacing={1.5} sx={{ flexWrap: "wrap" }}>
          <Link href="/pricing" target="_blank" rel="noopener">Pricing</Link>
          {Object.values(LEGAL_DOCS).map((d) => (
            <Link key={d.slug} href={`/legal/${d.slug}`} target="_blank" rel="noopener">{d.title}</Link>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
}

function DocumentEditor({ ns, kind, doc }: { ns: string; kind: DocumentKind; doc: OperatorDocument | null }) {
  const identity = useIdentity();
  const qc = useQueryClient();
  const own = doc && !doc.inherited ? doc.body : "";
  const [body, setBody] = useState(own);
  useEffect(() => setBody(own), [own]);
  const input = useRef<HTMLTextAreaElement | null>(null);

  const save = useMutation({
    mutationFn: async () => (await saveDocument(identity, ns, kind, body)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operatorDocuments", ns] }),
  });

  const text = normaliseDocument(body);
  const problem = documentProblem(body);
  const dirty = text !== normaliseDocument(own);
  const removing = dirty && text === "" && own !== "";
  const placeholders = remainingPlaceholders(text);

  // Select a placeholder in the text. The caret goes there first and the field
  // is focused again, because a browser scrolls a textarea to its caret on focus.
  const find = (placeholder: string) => {
    const el = input.current;
    if (!el) return;
    const at = el.value.indexOf(placeholder);
    if (at < 0) return;
    el.focus();
    el.setSelectionRange(at, at);
    el.blur();
    el.focus();
    el.setSelectionRange(at, at + placeholder.length);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>{DOCUMENT_TITLES[kind]}</Typography>
        {doc && !doc.inherited && <Chip size="small" color="success" label="On your portal" />}
        {doc?.inherited && <Chip size="small" variant="outlined" label={`from ${doc.namespace}`} />}
        {!doc && <Chip size="small" variant="outlined" label="Not on your portal" />}
      </Stack>

      {kind === "pricing" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Your plans and prices are listed on the portal automatically, from the plans on sale. Add a note for below them:
          taxes, fair use, when validity starts.
        </Typography>
      )}
      {kind === "terms" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Your terms with your guests. AnkhOS offers a starting template (its subscriber Usage Policy, approved 2026-09-07, legal
          review pending). It is not legal advice: adapt it to your licence and have it reviewed.
        </Typography>
      )}
      {doc?.inherited && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Your portal shows the document from <b>{doc.namespace}</b>. Write your own here to replace it for {ns}.
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        minRows={kind === "pricing" ? 3 : 8}
        maxRows={24}
        value={body}
        inputRef={input}
        placeholder={doc?.inherited ? doc.body.slice(0, 400) : ""}
        onChange={(e) => {
          save.reset();
          setBody(e.target.value);
        }}
        error={problem !== null}
        helperText={problem ?? `${[...text].length}/${MAX_DOCUMENT_CHARS} characters`}
        slotProps={{ htmlInput: { "aria-label": DOCUMENT_TITLES[kind] } }}
      />
      {placeholders.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1 }} role="status">
          <AlertTitle>
            Save is off until every «placeholder» is replaced: {placeholders.length} still in the text
          </AlertTitle>
          Click one to find it. Replace the whole marker, including « and ».
          <Stack direction="row" useFlexGap spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} aria-label="Placeholders still in the text">
            {placeholders.map((p) => (
              <Chip key={p} size="small" variant="outlined" label={p.length > 40 ? `${p.slice(0, 40)}…` : p} onClick={() => find(p)} />
            ))}
          </Stack>
        </Alert>
      )}

      {save.error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          <AlertTitle>Not saved</AlertTitle>
          {refusal(save.error)}
        </Alert>
      )}
      {save.isSuccess && !dirty && <Alert severity="success" sx={{ mt: 1 }}>Saved. Guests see it on their next portal load, within a minute.</Alert>}

      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }} useFlexGap>
        <Button
          variant="contained"
          color={removing ? "error" : "primary"}
          disabled={!dirty || problem !== null || placeholders.length > 0 || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : removing ? "Remove from portal" : "Save"}
        </Button>
        {kind === "terms" && text === "" && (
          <Button variant="outlined" onClick={() => setBody(SUBSCRIBER_TERMS_TEMPLATE)}>Start from the AnkhOS template</Button>
        )}
        {dirty && <Button onClick={() => setBody(own)}>Discard changes</Button>}
      </Stack>
    </Paper>
  );
}
